/**
 * Shared e1RM → working-weight anchor.
 *
 * Single source of truth used by BOTH prescription engines:
 *   - smartWorkoutGenerator (program-prescribed lifts)
 *   - unifiedRecommendation (exercise swaps + freestyle)
 *
 * For a recognized primary barbell lift, the working weight is derived from
 * the user's current e1RM through the RPE/RIR → %1RM table — so a swapped or
 * freestyle squat is priced the same way as a program squat, and an advanced
 * lifter never gets an empty-bar estimate when their 1RM is on file.
 */

import { roundToNearestPlate } from './plateMath';
import { rpeToWorkingWeight, getCanonical1RMKey } from './oneRM';

// Recognized primary barbell lifts whose working weight should be ANCHORED to
// the user's e1RM rather than last-used weight or a generic estimate.
// Bodyweight/pattern lifts (pull-ups etc.) are intentionally excluded — their
// stored "1RM" is a rep count or added load, not a barbell working max.
export const PRIMARY_LIFT_KEYS = new Set([
  'backSquat', 'frontSquat', 'benchPress', 'inclineBenchPress', 'overheadPress',
  'deadlift', 'sumoDeadlift', 'romanianDeadlift', 'trapBarDeadlift', 'deficitDeadlift',
  'barbellRow', 'powerClean', 'powerSnatch', 'hipThrust',
]);

/** True when the exercise name resolves to a recognized primary barbell lift. */
export function isPrimaryLift(exerciseName: string): boolean {
  return PRIMARY_LIFT_KEYS.has(getCanonical1RMKey(exerciseName));
}

/**
 * Case/space/punctuation-insensitive 1RM lookup that returns the MAX matching
 * value (mirrors lookup1RM in smartWorkoutGenerator — kept here so the shared
 * anchor has no dependency back on that module). Tolerant of the duplicate-key
 * data that the de-dup migration is still cleaning up across users.
 */
export function lookupE1RM(oneRMs: Record<string, number>, exerciseName: string): number {
  if (!exerciseName) return 0;
  if (oneRMs[exerciseName] && oneRMs[exerciseName] > 0) return oneRMs[exerciseName];
  const norm = (s: string) => s.toLowerCase().replace(/[\s\-_()]+/g, '');
  const target = norm(exerciseName);
  let best = 0;
  for (const key in oneRMs) {
    const v = oneRMs[key];
    if (v && v > 0 && norm(key) === target && v > best) best = v;
  }
  return best;
}

/** True when the intensity string carries an explicit %1RM (already anchored). */
export function hasExplicitPercent(intensity?: string): boolean {
  return !!(intensity && /\d+\s*%/.test(intensity));
}

/**
 * Parse the prescribed RPE from an intensity string like "RPE 8" or "RPE 7-8".
 * Returns the top of a range — that's the working target.
 */
export function parseRpe(intensity: string): number | null {
  const m = intensity.match(/rpe\s*(\d+(?:\.\d+)?)(?:\s*-\s*(\d+(?:\.\d+)?))?/i);
  if (!m) return null;
  return parseFloat(m[2] ?? m[1]);
}

/** A prescription's effort/rep context, as much as a caller can supply. */
export interface Prescription {
  intensity?: string;
  sets_reps?: string;
  /** Fallback rep target when sets_reps can't be parsed. */
  reps?: number;
}

/**
 * Derive a working weight from a known e1RM, honoring (in order) an explicit
 * %1RM, an RPE prescription, an RIR prescription, or a qualitative cue
 * ("light"/"moderate"/"heavy"). Defaults to ~RPE 7.5 for an unspecified
 * working set.
 */
export function deriveWorkingLoadFrom1RM(oneRM: number, p: Prescription): number {
  const intensity = (p.intensity || '').trim();
  const repsMatch = (p.sets_reps || '').match(/\d+\s*[x×]\s*(\d+)/i);
  const reps = repsMatch ? parseInt(repsMatch[1]) : (p.reps ?? 8);

  // Explicit percentage, e.g. "75%"
  const pctMatch = intensity.match(/(\d+)\s*%/);
  if (pctMatch) {
    return roundToNearestPlate(Math.round(oneRM * (parseInt(pctMatch[1]) / 100)));
  }

  // RPE prescription, e.g. "RPE 8" / "RPE 7-8"
  const rpe = parseRpe(intensity);
  if (rpe !== null) {
    return roundToNearestPlate(rpeToWorkingWeight(oneRM, reps, rpe));
  }

  // RIR prescription, e.g. "RIR 2" / "2 RIR" → RPE = 10 − RIR (floored at 4).
  const rirMatch = intensity.match(/(\d+(?:\.\d+)?)\s*rir|rir\s*(\d+(?:\.\d+)?)/i);
  if (rirMatch) {
    const rir = parseFloat(rirMatch[1] ?? rirMatch[2]);
    return roundToNearestPlate(rpeToWorkingWeight(oneRM, reps, Math.max(4, 10 - rir)));
  }

  // Qualitative cue → approximate RPE (check 'heavy' before 'moderate' so
  // "moderate-heavy" reads as the harder cue).
  const lower = intensity.toLowerCase();
  const qualitativeRpe =
    lower.includes('heavy') ? 8 :
    lower.includes('moderate') ? 7 :
    lower.includes('light') ? 6 :
    7.5; // unspecified working set
  return roundToNearestPlate(rpeToWorkingWeight(oneRM, reps, qualitativeRpe));
}
